import { EventUserStatus } from '@graphql/generated-base';
import { useAuth } from '@shared/components/AuthProvider';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';

type UseGuestGuideVisibilityParams = Readonly<{
  sessionData:
    | {
        eventByName?: {
          activeSession?: {
            eventCTA?: {
              rsvpIsCompleted?: boolean;
            };
          };
        } | null;
      }
    | null
    | undefined;
  ctaQueryCalled: boolean;
  ctaQueryLoading: boolean;
  shouldDisplayUnlockButton: boolean;
}>;

/**
 * Determines if GuestGuide should be shown.
 * - If showGuestGuide feature flag is true: always show (overrides other conditions)
 * - If showGuestGuide feature flag is false: always hide (overrides other conditions)
 * - If showGuestGuide feature flag is undefined: show when all of these are true:
 *   - CTA query has been called
 *   - Not loading event info or CTA data
 *   - Event info exists
 *   - User is not admin or owner
 *   - Event has not passed
 *   - Unlock button should not be displayed
 *   - RSVP is completed
 *   - No live stream is currently displayed (hide GuestGuide when stream is active, same as ActionBar)
 */
export const useGuestGuideVisibility = ({ sessionData, ctaQueryCalled, ctaQueryLoading, shouldDisplayUnlockButton }: UseGuestGuideVisibilityParams): boolean => {
  const { eventInfo, loading: loadingEventInfo } = useEventInfo();
  const { currentUser } = useAuth();
  const [showGuestGuide] = useIsFeatureEnabled('showGuestGuide');

  const hasEventPast = (eventInfo?.finalizedDate?.milliseconds || Infinity) < Date.now();

  const isAdminOrOwner = ([EventUserStatus.admin, EventUserStatus.owner] as Maybe<EventUserStatus>[]).includes(currentUser.activeEventUser?.status);
  const isRsvped = sessionData?.eventByName?.activeSession?.eventCTA?.rsvpIsCompleted;

  // showGuestGuide overrides other conditions when defined (true = show)
  // When undefined, use original logic: guide is hidden when any of these conditions are true
  if (showGuestGuide) {
    return showGuestGuide;
  }

  const shouldHide = [!ctaQueryCalled, loadingEventInfo, ctaQueryLoading, !eventInfo, isAdminOrOwner, hasEventPast, shouldDisplayUnlockButton, !isRsvped].some(Boolean);
  return !shouldHide;
};
